// src/components/SectionRow.jsx
import React, { useState } from "react";
import { GripVertical, ChevronDown, MoreVertical } from "lucide-react";
import { useBuilderStore } from "../store/builderStore";

/**
 * SafetyCulture-style Section header row.
 *
 * This is a PRESENTATIONAL component only.
 * It relies on props so we can wire it to builderStore and DnD later.
 *
 * Props:
 *  - section: { id, title, description, repeatable? }
 *  - isActive: boolean           // e.g. currently selected section
 *  - isCollapsed: boolean        // if true, body/questions are hidden
 *  - dragHandleProps: object     // from @hello-pangea/dnd
 *  - onToggleCollapse(): void
 *  - onTitleChange(newTitle: string): void
 *  - onDescriptionChange(newDesc: string): void
 *  - onToggleRepeat(): void
 *  - onDuplicate(): void
 *  - onDelete(): void
 *  - onClick(): void             // called when header row is clicked
 *  - isPage?: boolean            // if true, this header represents a Page (not an in-page section)
 */
export default function SectionRow({
  section,
  isActive = false,
  isCollapsed = false,
  dragHandleProps = {},
  onToggleCollapse,
  onTitleChange,
  onDescriptionChange,
  onToggleRepeat,
  onDuplicate,
  onDelete,
  onClick,
  isPage = false,
  indentLevel = 0,
  rowRef = null,
}) {
  const title =
    (section?.title || "").trim() ||
    (isPage ? "Untitled page" : "Untitled section");
  const description = section?.description || "";
  const repeatable = !!section?.repeatable || !!section?.repeatSection;
  const [menuOpen, setMenuOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const setActiveRow = useBuilderStore((s) => s.setActiveRow);

  const handleHeaderClick = (e) => {
    // Row click should ONLY select — do NOT collapse.
    const tag = e.target.tagName;
    if (tag === "INPUT" || tag === "BUTTON" || tag === "TEXTAREA") return;

    if (setActiveRow) {
      setActiveRow(section?.id);
    }
    onClick && onClick();
    // (collapse only happens when clicking the chevron)
  };

  return (
    <div
      className="indent-wrapper"
      style={{ paddingLeft: indentLevel * 16 }}
      ref={rowRef}
      data-row-id={section?.id}
    >
      {indentLevel > 0 && (
        <div
          className="indent-line"
          style={{
            left: (indentLevel - 1) * 16 - 8,
          }}
        />
      )}
      <div
        className={`section-card section-row ${isActive ? "section-row-active" : ""} ${
          isCollapsed ? "section-row-collapsed" : ""
        }`}
        style={{
          paddingBottom: description ? 16 : 12,
          background: isActive || isHovered ? "#dbeafe" : "#e0ecff",
          borderRadius: 10,
          border: isActive ? "1px solid #60a5fa" : "1px solid #bfdbfe",
          boxShadow: isActive
            ? "0 0 0 1px rgba(37,99,235,0.4), 0 4px 10px rgba(15,23,42,0.18)"
            : "0 1px 2px rgba(129,140,248,0.35)",
          position: "relative",
          borderLeft: isActive ? "3px solid #2563eb" : "3px solid transparent",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
      {/* no indent line for Section headers */}
      {/* Header line: chevron, drag handle, icon, title, repeat, menu */}
      <div
        className="section-header"
        onClick={handleHeaderClick}
        style={{ cursor: "pointer" }}
      >
        {/* Collapse / expand chevron */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse && onToggleCollapse();
          }}
          style={{
            border: "none",
            background: "transparent",
            padding: 6,
            marginRight: 4,
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-label={isCollapsed ? "Expand section" : "Collapse section"}
        >
          <ChevronDown
            size={18}
            style={{
              transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
              transition: "transform 0.15s ease",
              color: "#4b5563",
            }}
          />
        </button>

        {/* Drag handle */}
        <span
          className="drag-handle"
          {...dragHandleProps}
          onClick={(e) => e.stopPropagation()}
          style={{
            padding: 6,
            width: 28,
            height: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <GripVertical size={18} />
        </span>

        {/* Section icon + title input */}
        <div className="section-title" style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 999,
              background: "#eef2ff",
              color: "#4f46e5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {isPage ? "P" : "S"}
          </div>
          <input
            type="text"
            value={title}
            onChange={(e) => onTitleChange && onTitleChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={isPage ? "Page title" : "Section title"}
            className="section-title-input"
            style={{
              border: "none",
              background: "transparent",
              fontSize: "1.02rem",
              fontWeight: 600,
              outline: "none",
              width: "100%",
            }}
          />
        </div>

        {/* Right-side controls: repeat + menu */}
        <div
          className="section-header-actions"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginLeft: 8,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Repeat this section toggle (NOT shown for pages) */}
          {!isPage && (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                color: "#4b5563",
                userSelect: "none",
                whiteSpace: "nowrap",
              }}
            >
              <input
                type="checkbox"
                checked={repeatable}
                onChange={() => onToggleRepeat && onToggleRepeat()}
              />
              <span>Repeat this section</span>
            </label>
          )}

          {/* 3-dot menu with basic options */}
          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="icon-btn"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((open) => !open);
              }}
              aria-label="Section options"
            >
              <MoreVertical size={16} />
            </button>
            {menuOpen && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "100%",
                  marginTop: 4,
                  minWidth: 160,
                  background: "#ffffff",
                  borderRadius: 8,
                  boxShadow: "0 10px 25px rgba(15,23,42,0.15)",
                  padding: 4,
                  zIndex: 20,
                }}
              >
                <button
                  type="button"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "none",
                    background: "transparent",
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isPage && onDuplicate) {
                      onDuplicate();
                    }
                    // For pages, "Move page" is just informational; movement is via drag handle.
                    setMenuOpen(false);
                  }}
                >
                  {isPage ? "Move page" : "Duplicate section"}
                </button>
                <button
                  type="button"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "none",
                    background: "transparent",
                    fontSize: 13,
                    cursor: "pointer",
                    color: "#b91c1c",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete && onDelete();
                    setMenuOpen(false);
                  }}
                >
                  {isPage ? "Delete page" : "Delete section"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Optional description input under header */}
      <div style={{ marginTop: 6 }}>
        <textarea
          value={description}
          onChange={(e) => onDescriptionChange && onDescriptionChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          placeholder={
            isPage
              ? "Add a description for this page (optional)…"
              : "Add a description for this section (optional)…"
          }
          className="section-desc-input"
          rows={description ? 2 : 1}
          style={{
            width: "100%",
            resize: "none",
            border: "none",
            outline: "none",
            fontSize: "0.9rem",
            color: "#6b7280",
            background: "transparent",
          }}
        />
      </div>
      </div>
    </div>
  );
}
